import { useEffect, useRef, useState } from "react";
import { Sparkles, Send } from "lucide-react";
import { puroMitraHistory, puroMitraChat, apiErr } from "../lib/api";
import { useCustomer } from "../lib/auth";
import { toast } from "sonner";

const QUICK = [
  { kn: "ಸತ್ಯನಾರಾಯಣ ಪೂಜೆಗೆ ಏನು ಸಾಮಗ್ರಿ ಬೇಕು?", en: "Satyanarayana pooja samagri list?" },
  { kn: "ಗೃಹಪ್ರವೇಶಕ್ಕೆ ಹೇಗೆ ಸಿದ್ಧತೆ ಮಾಡಬೇಕು?", en: "How to prepare for Griha Pravesha?" },
  { kn: "ಪ್ರಸ್ತಾವನೆ (proposals) ಹೇಗೆ ಪಡೆಯುವುದು?", en: "How do I get proposals?" },
];

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`puromitra-msg-${role}`}>
      <div
        className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "rounded-br-md bg-[#8B1A2B] text-white"
            : "rounded-bl-md border border-[#EBE0D0] bg-white text-[#2A211C]"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

export default function PuroMitra() {
  const { user, ready, setAuthOpen } = useCustomer();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    puroMitraHistory().then((d) => setMsgs(d.messages || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setSending(true);
    try {
      const d = await puroMitraChat(q);
      setMsgs((m) => [...m, { role: "assistant", content: d.reply }]);
    } catch (e) {
      toast.error(apiErr(e, "PuroMitra could not reply"));
      setMsgs((m) => [...m, { role: "assistant", content: "ಕ್ಷಮಿಸಿ, ಈಗ ಉತ್ತರಿಸಲು ಆಗುತ್ತಿಲ್ಲ. Sorry, I couldn't reply just now — please try again." }]);
    } finally {
      setSending(false);
    }
  };

  if (ready && !user) {
    return (
      <div className="px-4 pt-10 text-center" data-testid="puromitra-signin">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F5E9DA]">
          <Sparkles size={26} className="text-[#8B1A2B]" />
        </div>
        <h1 className="mt-3 font-heading text-2xl text-[#2A211C]">PuroMitra</h1>
        <p className="mt-1 text-sm text-[#7A6E64]">ನಿಮ್ಮ ಪೂಜಾ ಸಹಾಯಕ · Your pooja assistant. Sign in to start chatting.</p>
        <button onClick={() => setAuthOpen(true)} data-testid="puromitra-signin-btn" className="mt-4 rounded-xl bg-[#8B1A2B] px-5 py-2.5 text-sm font-semibold text-white">
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-[calc(100vh-11rem)] max-w-3xl flex-col md:h-[calc(100vh-8rem)]" data-testid="cust-puromitra">
      <div className="border-b border-[#EBE0D0] bg-[#FBF6EF] px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#8B1A2B]">
            <Sparkles size={18} className="text-white" />
          </div>
          <div>
            <h1 className="font-heading text-lg leading-tight text-[#2A211C]">PuroMitra</h1>
            <p className="text-[11px] text-[#7A6E64]">ಪೂಜಾ ಮಾರ್ಗದರ್ಶಕ · Rituals & samagri helper</p>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {msgs.length === 0 && !sending && (
          <div className="rounded-2xl border border-[#EBE0D0] bg-white p-4">
            <p className="text-sm text-[#2A211C]">ನಮಸ್ಕಾರ 🙏 ನಾನು PuroMitra. ಪೂಜೆ, ಸಾಮಗ್ರಿ, ಮುಹೂರ್ತ ಅಥವಾ ಪುರೋಹಿತರನ್ನು ಆಯ್ಕೆ ಮಾಡುವ ಬಗ್ಗೆ ಕೇಳಿ.</p>
            <p className="mt-1 text-xs text-[#7A6E64]">Ask me about any pooja, its samagri, how to prepare, or how to get proposals.</p>
          </div>
        )}
        {msgs.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} />
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="puromitra-typing">
            <div className="flex gap-1 rounded-2xl rounded-bl-md border border-[#EBE0D0] bg-white px-4 py-3">
              {[0, 1, 2].map((d) => (
                <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-[#C9A24A]" style={{ animationDelay: `${d * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {msgs.length === 0 && (
        <div className="flex flex-wrap gap-2 px-4 pb-2">
          {QUICK.map((qp, i) => (
            <button
              key={i}
              data-testid={`puromitra-quick-${i}`}
              onClick={() => send(qp.kn)}
              className="rounded-full border border-[#EBE0D0] bg-white px-3 py-1.5 text-left text-xs text-[#5A4A3F] transition-colors hover:border-[#8B1A2B] hover:text-[#8B1A2B]"
            >
              {qp.en}
            </button>
          ))}
        </div>
      )}

      <div className="border-t border-[#EBE0D0] bg-white px-3 py-2.5">
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-end gap-2"
        >
          <textarea
            data-testid="puromitra-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="ಇಲ್ಲಿ ಟೈಪ್ ಮಾಡಿ · Type your question…"
            className="max-h-28 flex-1 resize-none rounded-2xl border border-[#EBE0D0] bg-[#FBF6EF] px-3.5 py-2.5 text-sm text-[#2A211C] outline-none focus:border-[#8B1A2B]"
          />
          <button
            type="submit"
            data-testid="puromitra-send-btn"
            disabled={sending || !input.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#8B1A2B] text-white transition-opacity disabled:opacity-40"
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}
